import React, { useEffect, useState } from 'react'
import { AdvancedImage } from '@cloudinary/react'
import { RadioGroup } from '@headlessui/react'
import { Plus, StarIcon } from 'lucide-react'
import {
	type DataFunctionArgs,
	json,
	type LoaderFunction,
	redirect,
	type V2_MetaFunction,
} from '@remix-run/node'
import { Form, useLoaderData, useNavigation } from '@remix-run/react'
import clsx from 'clsx'
import { invariant } from '~/utils/misc.ts'
import { Button } from '~/components/ui/button.tsx'
import { SelectCommand } from '~/components/SelectCommand.tsx'
import type {
	GalleryImage,
	Price,
	ProductColor,
	ProductSize,
	ProductVariant,
	Scene,
	Style,
} from '@prisma/client'
import cld from '~/services/cloudinary/cloudinaryInstance.tsx'
import { TooltipStandard } from '~/components/Tooltip/TooltipStandard.tsx'
import { TooltipPopover } from '~/components/Tooltip/TooltipPopover.tsx'
import { Skeleton } from '~/components/ui/skeleton.tsx'
import { SelectWithImage } from '~/components/ProductBuilder/SelectWithImage.tsx'
import { GeneralErrorBoundary } from '~/components/error-boundary.tsx'
import { makeTimings, time } from '~/utils/timing.server.ts'
import { prisma } from '~/utils/db.server.ts'
import { getCartId } from '~/utils/cart.server.ts'
import { getUserId } from '~/utils/auth.server.ts'

// TODO: add zod and conform to form
// TODO: allow user to upload one photo on this page, and then upload more on the next page. Resource route fetcher.
// allowing the user to upload a photo on this page will allow for us to use it for a preview

function findLowestPricedVariation(
	variations: ProductVariant[],
	prices: Price[],
): ProductVariant {
	return variations.reduce((lowest, variation) => {
		const price = prices.find(price => price.id === variation.priceId)
		const lowestPrice = prices.find(price => price.id === lowest.priceId)

		return price && lowestPrice && price.amount < lowestPrice.amount
			? variation
			: lowest
	}, variations[0])
}

function selectUpToThreeImages(images: GalleryImage[]): GalleryImage[] {
	return images.slice(0, 3)
}

export const meta: V2_MetaFunction = () => {
	return [{ title: 'Product - PetCanvas' }]
}

export const loader: LoaderFunction = async ({ request, params }) => {
	const timings = makeTimings('store/product loader')
	invariant(params, 'params is required')
	const productId = params.productId
	invariant(productId, 'params.productId is required')

	const product = await time(
		() =>
			prisma.product.findUnique({
				where: {
					id: productId,
				},
			}),
		{ timings, type: 'get product by ID' },
	)
	const variations = await time(
		() =>
			prisma.productVariant.findMany({
				where: {
					productId: productId,
				},
			}),
		{ timings, type: 'get product variations by product ID' },
	)
	const styles = await time(() => prisma.style.findMany(), {
		timings,
		type: 'get styles',
	})
	const scenes = await time(() => prisma.scene.findMany(), {
		timings,
		type: 'get scenes',
	})
	const allColors = await time(() => prisma.productColor.findMany(), {
		timings,
		type: 'get product colors',
	})
	const sizes = await time(() => prisma.productSize.findMany(), {
		timings,
		type: 'get product sizes',
	})
	const prices = await time(() => prisma.price.findMany(), {
		timings,
		type: 'get prices',
	})
	const exampleImages = await time(() => prisma.galleryImage.findMany(), {
		timings,
		type: 'get gallery images',
	})

	const regularStyles = styles.filter(style => !style.specialType)
	const specialStyles = styles.filter(style => style.specialType)
	const regularScenes = scenes.filter(scene => !scene.specialType)
	const specialScenes = scenes.filter(scene => scene.specialType)

	const productColors = allColors.filter(color =>
		variations.some(variation => variation.colorId === color.id),
	)

	const lowestPricedVariation = findLowestPricedVariation(variations, prices)

	return json(
		{
			product,
			variations,
			regularStyles,
			specialStyles,
			regularScenes,
			specialScenes,
			colors: productColors,
			sizes,
			prices,
			exampleImages,
			lowestPricedVariation,
		},
		{
			headers: { 'Server-Timing': timings.toString() },
		},
	)
}

export async function action({ request, params }: DataFunctionArgs) {
	invariant(params, 'params is required')
	invariant(params.productId, 'params.productId is required')

	let userId = await getUserId(request)

	const { cartId } = await getCartId(request)

	const body = new URLSearchParams(await request.text())
	const selectedVariantId = body.get('selectedVariantId') as string
	const selectedSceneId = body.get('selectedSceneId') as string
	const selectedStyleId = body.get('selectedStyleId') as string
	const userNotesInput = body.get('userNotes') as string
	const styleNotesInput = body.get('userStyleNotes') as string
	const sceneNotesInput = body.get('userSceneNotes') as string

	// TODO: Set based on user vs guest
	// guestUserId: null,

	const newCart = {
		id: cartId,
		userNotes: userNotesInput,
		styleNotes: styleNotesInput,
		sceneNotes: sceneNotesInput,
		createdAt: new Date(),
		updatedAt: new Date(),
		isAnonymous: false, // TODO: Set based on user vs guest
		userImages: null,
	}

	const cart = await prisma.cart.upsert({
		where: {
			id: cartId,
		},
		update: {
			...newCart,
			product: {
				connect: {
					id: params.productId,
				},
			},
			productVariant: {
				connect: {
					id: selectedVariantId,
				},
			},
			scene: {
				connect: {
					id: selectedSceneId,
				},
			},
			style: {
				connect: {
					id: selectedStyleId,
				},
			},
		},
		create: {
			...newCart,
			product: {
				connect: {
					id: params.productId,
				},
			},
			productVariant: {
				connect: {
					id: selectedVariantId,
				},
			},
			scene: {
				connect: {
					id: selectedSceneId,
				},
			},
			style: {
				connect: {
					id: selectedStyleId,
				},
			},
		},
	})
	console.log('cart', cart)

	return redirect(`/store/user-image-upload`)
}

function findSelectedVariation(
	variations: ProductVariant[],
	selectedColor: ProductColor,
	selectedSize: ProductSize,
): ProductVariant {
	const foundVariation = variations.find(
		variation =>
			variation.colorId === selectedColor.id &&
			variation.sizeId === selectedSize.id,
	)
	return foundVariation ? foundVariation : variations[0]
}

function findSelectedVariationPriceAmount(
	prices: Price[],
	selectedVariant: ProductVariant,
): number {
	const foundPrice = prices.find(price => price.id === selectedVariant.priceId)
	return foundPrice?.amount ? foundPrice.amount : 0
}

function filterExampleImages(
	exampleImages: GalleryImage[],
	selectedStyle: Style | null,
	selectedScene: Scene | null,
): GalleryImage[] {
	// filter example images by style and/or scene
	// default is to return all example images
	// if style or scene is given, filter by that, if both are given, filter by both
	let filteredExampleImages = exampleImages
	if (selectedStyle) {
		filteredExampleImages = filteredExampleImages.filter(
			image => image.styleName === selectedStyle.label,
		)
	}
	if (selectedScene) {
		filteredExampleImages = filteredExampleImages.filter(
			image => image.sceneName === selectedScene.label,
		)
	}

	return filteredExampleImages
}

function generateProductImg(imgSrc: string) {
	return cld.image(imgSrc).quality('auto').format('auto')
}

function generateThumbnail(imgSrc: string) {
	return cld.image(imgSrc).quality('auto').format('auto')
	// .resize(thumbnail().width(100).height(100).crop('fill'))
}

export default function ProductDetailPage() {
	const data = useLoaderData<typeof loader>()
	const navigation = useNavigation()

	const submitting = navigation.state !== 'idle'
	const [formValid, setFormValid] = useState(false)

	const [selectedColor, setSelectedColor] = useState(() => {
		const color = data.colors.find(
			color => color.id === data.lowestPricedVariation.colorId,
		)
		return color ? color : data.colors[0]
	})

	const [selectedSize, setSelectedSize] = useState(() => {
		const size = data.sizes.find(
			size => size.id === data.lowestPricedVariation.sizeId,
		)
		return size ? size : data.sizes[0]
	})

	const [selectedVariant, setSelectedVariation] = useState(
		data.lowestPricedVariation,
	)
	const [selectedStyle, setSelectedStyle] = useState<Style | null>(null)
	const [selectedScene, setSelectedScene] = useState<Scene | null>(null)
	const [displayUserSceneNotes, setDisplayUserSceneNotes] = useState(false)
	const [displayUserStyleNotes, setDisplayUserStyleNotes] = useState(false)
	const [exampleImagesStyle, setExampleImagesStyle] = useState(() =>
		filterExampleImages(data.exampleImages, selectedStyle, null),
	)
	const [exampleImagesScene, setExampleImagesScene] = useState(() =>
		filterExampleImages(data.exampleImages, null, selectedScene),
	)

	useEffect(() => {
		if (selectedStyle) {
			setExampleImagesStyle(
				selectUpToThreeImages(
					filterExampleImages(data.exampleImages, selectedStyle, null),
				),
			)
		} else {
			setExampleImagesStyle([])
		}
	}, [data.exampleImages, selectedStyle])

	useEffect(() => {
		if (selectedScene) {
			setExampleImagesScene(
				selectUpToThreeImages(
					filterExampleImages(data.exampleImages, null, selectedScene),
				),
			)
		} else {
			setExampleImagesScene([])
		}
	}, [data.exampleImages, selectedScene])

	const updateFormValidity = () => {
		setFormValid(!!(selectedVariant && selectedStyle && selectedScene))
	}

	useEffect(() => {
		updateFormValidity()
	}, [selectedVariant, selectedStyle, selectedScene])

	const handleStyleSelect = (selectedOption: Option<Style>) => {
		setSelectedStyle(selectedOption)
		setDisplayUserStyleNotes(true)
	}

	const handleSceneSelect = (selectedOption: Option<Scene>) => {
		setSelectedScene(selectedOption)
		setDisplayUserSceneNotes(true)
	}

	useEffect(() => {
		const newSelectedVariation = findSelectedVariation(
			data.variations,
			selectedColor,
			selectedSize,
		)
		if (newSelectedVariation) {
			setSelectedVariation(newSelectedVariation)
		}
	}, [data.variations, selectedColor, selectedSize])

	return (
		<div className="bg-lightGray antialiased">
			<div className="pb-16 pt-6 sm:pb-24">
				<nav
					aria-label="Breadcrumb"
					className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8"
				>
					<ol role="list" className="flex items-center space-x-4">
						<li className="text-sm">
							<a
								href={data.product.href}
								aria-current="page"
								className="font-medium text-gray-500 hover:text-gray-600"
							>
								{data.product.name}
							</a>
						</li>
					</ol>
				</nav>
				<div className="mx-auto mt-8 px-4 sm:px-6 lg:max-w-7xl lg:px-8">
					<div className="lg:grid lg:auto-rows-min lg:grid-cols-12 lg:gap-x-8">
						<div className="lg:col-span-5 lg:col-start-8">
							<div className="flex justify-between">
								<h1 className="text-xl font-medium text-gray-900">
									{data.product.name}
								</h1>
								<p className="text-xl font-medium text-gray-600">
									{'$'}
									{(
										findSelectedVariationPriceAmount(
											data.prices,
											selectedVariant,
										) / 100
									).toFixed(2)}
								</p>
							</div>
							{/* Reviews */}
							<div className="mt-4">
								<h2 className="sr-only">Reviews</h2>
								<div className="flex items-center">
									<p className="text-sm text-gray-700">
										{data.product.rating}
										<span className="sr-only"> out of 5 stars</span>
									</p>
									<div className="ml-1 flex items-center">
										{[0, 1, 2, 3, 4].map(rating => (
											<StarIcon
												key={rating}
												className={clsx(
													data.product.rating > rating
														? 'text-yellow-400'
														: 'text-gray-200',
													'h-5 w-5 flex-shrink-0',
												)}
												aria-hidden="true"
											/>
										))}
									</div>
									<div
										aria-hidden="true"
										className="ml-4 text-sm text-gray-300"
									>
										·
									</div>
									<div className="ml-4 flex">
										<p className="text-sm font-medium text-indigo-600 hover:text-indigo-500">
											{data.product.reviewCount} reviews
										</p>
									</div>
								</div>
							</div>
						</div>

						{/* Image gallery */}
						<div className="mt-8 lg:col-span-7 lg:col-start-1 lg:row-span-3 lg:row-start-1 lg:mt-0">
							<h2 className="sr-only">Images</h2>

							<div className="grid grid-cols-1 lg:grid-cols-2 lg:grid-rows-2 lg:gap-8">
								<AdvancedImage
									key={data.product.id}
									cldImg={generateProductImg(selectedVariant.imgSrc)}
									alt={selectedVariant.imgAlt}
									className="rounded-lg lg:col-span-2 lg:row-span-2 lg:block"
								/>
							</div>

							{/* Scene and Style Selection Display */}
							<section
								aria-labelledby="options-heading"
								className="lg:col-span-7 lg:mt-0"
							>
								<div className="mt-12 grid grid-cols-12 lg:items-start lg:gap-x-12 xl:gap-x-16">
									<div className="col-span-5">
										<h2 className="text-lg font-medium text-gray-900">
											Selected Style
										</h2>
										<div className="mt-4">
											{selectedStyle ? (
												<AdvancedImage
													cldImg={
														selectedStyle
															? generateProductImg(selectedStyle.imgSrc)
															: generateProductImg(
																	'/pet-project/square_2d_fluibz',
															  )
													}
													alt="Style Image"
													className="h-40 w-40 rounded-md bg-gray-200 object-fill object-center lg:h-auto lg:w-full"
												/>
											) : (
												<div className="flex h-40 w-40 items-center justify-center bg-gray-50 lg:h-60 lg:w-full">
													<Skeleton className="h-36 w-36 rounded-md bg-gray-200/25 lg:h-48 lg:w-48" />
												</div>
											)}
											<p className="mt-4 text-sm text-gray-600 lg:mt-6">
												{selectedStyle
													? selectedStyle.label
													: 'Select a Style First'}
											</p>
										</div>
									</div>
									<div className="col-span-2 flex flex-col items-center justify-center">
										<div className="hidden lg:flex">
											<Plus className="h-8 w-8 text-gray-600" />
										</div>
										<div className="my-4 flex lg:hidden">
											<Plus className="h-8 w-8 text-gray-600" />
										</div>
									</div>
									<div className="col-span-5">
										<h2 className="text-lg font-medium text-gray-900">
											Selected Scene
										</h2>
										<div className="mt-4">
											{selectedScene ? (
												<AdvancedImage
													cldImg={
														selectedScene
															? generateProductImg(selectedScene.imgSrc)
															: generateProductImg(
																	'/pet-project/square_2d_fluibz',
															  )
													}
													alt="Scene Image"
													className="h-40 w-40 rounded-md bg-gray-200 object-fill object-center lg:h-auto lg:w-full"
												/>
											) : (
												<div className="flex h-40 w-40 items-center justify-center bg-gray-50 lg:h-60 lg:w-full">
													<Skeleton className="h-36 w-36 rounded-md bg-gray-200/25 lg:h-48 lg:w-48" />
												</div>
											)}
											<p className="mt-4 text-sm text-gray-600 lg:mt-6">
												{selectedScene
													? selectedScene.label
													: 'Select a Scene First'}
											</p>
										</div>
									</div>
								</div>
							</section>
						</div>

						<div className="mt-8 lg:col-span-5">
							<Form method="post">
								<input
									type="hidden"
									name="selectedVariantId"
									value={selectedVariant.id}
								/>
								<input
									type="hidden"
									name="selectedSceneId"
									value={selectedScene?.id}
								/>
								<input
									type="hidden"
									name="selectedStyleId"
									value={selectedStyle?.id}
								/>

								{/* Color picker */}
								<div>
									<div className="flex flex-row justify-between">
										<h2 className="text-sm font-medium text-gray-900">Color</h2>
										<p className="text-sm font-medium text-gray-600">
											{selectedColor.name}
										</p>
									</div>

									<RadioGroup
										value={selectedColor}
										onChange={color => setSelectedColor(color)}
										className="mt-2"
									>
										<RadioGroup.Label className="sr-only">
											Choose a color
										</RadioGroup.Label>
										<div className="flex items-center space-x-3">
											{data.colors.map(color => (
												<RadioGroup.Option
													key={color.name}
													value={color}
													className={({ active, checked }) =>
														clsx(
															`bg-[${color.selectedColor}]`,
															active && checked ? 'ring ring-offset-1' : '',
															!active && checked ? 'ring-2' : '',
															'relative -m-0.5 flex cursor-pointer items-center justify-center rounded-full p-0.5 focus:outline-none',
														)
													}
												>
													<RadioGroup.Label as="span" className="sr-only">
														{color.name}
													</RadioGroup.Label>
													<span
														aria-hidden="true"
														className="h-8 w-8 rounded-full border border-black border-opacity-10"
														style={{ backgroundColor: color.bgColor }}
													/>
												</RadioGroup.Option>
											))}
										</div>
									</RadioGroup>
								</div>

								{/* Size picker */}
								<div className="mt-8">
									<div className="flex items-center justify-between">
										<h2 className="text-sm font-medium text-gray-900">Size</h2>
									</div>

									<RadioGroup
										value={selectedSize}
										onChange={setSelectedSize}
										className="mt-2"
									>
										<RadioGroup.Label className="sr-only">
											Choose a size
										</RadioGroup.Label>
										<div className="grid grid-cols-3 gap-3 sm:grid-cols-6">
											{data.sizes
												.map(size => {
													const variation = data.variations.find(
														variation =>
															variation.colorId === selectedColor.id &&
															variation.sizeId === size.id,
													)
													const price = data.prices.find(
														price => price.id === variation?.firestorePriceId,
													)

													return { size, variation, price }
												})
												.sort(
													(a, b) =>
														(a.price?.amount ?? 0) - (b.price?.amount ?? 0),
												)
												.filter(({ variation }) => variation)
												.map(({ size, variation }) => {
													const inStock = variation?.inStock

													return (
														<RadioGroup.Option
															key={size.name}
															value={size}
															className={({ active, checked }) =>
																clsx(
																	inStock
																		? 'cursor-pointer focus:outline-none'
																		: 'cursor-not-allowed opacity-25',
																	active
																		? 'ring-2 ring-indigo-500 ring-offset-2'
																		: '',
																	checked
																		? 'border-transparent bg-indigo-600 text-white hover:bg-indigo-700'
																		: 'border-gray-200 bg-white text-gray-900 hover:bg-gray-50',
																	'flex items-center justify-center rounded-md border px-3 py-3 text-sm font-medium uppercase sm:flex-1',
																)
															}
															disabled={!inStock}
														>
															<RadioGroup.Label as="span">
																{size.name}
															</RadioGroup.Label>
														</RadioGroup.Option>
													)
												})}
										</div>
									</RadioGroup>
								</div>

								{/* Style Select */}
								<div className="mt-8">
									<div className="flex items-center justify-between">
										<h2 className="text-sm font-medium text-gray-900">Style</h2>
										<div className="hidden lg:inline-block">
											<TooltipStandard
												content={
													'Style is largely about the style, technique, texture, and medium of the portrait.'
												}
											/>
										</div>
										<div className="inline-block lg:hidden">
											<TooltipPopover
												title={'Style'}
												content={
													'Style is largely about the style, technique, texture, and medium of the portrait.'
												}
											/>
										</div>
									</div>
									<div className="hidden lg:inline-block">
										<SelectCommand<Style>
											options={data.regularStyles}
											specialOptions={data.specialStyles}
											onSelect={handleStyleSelect}
											autoFocus={false}
										/>
									</div>
									<div className="lg:hidden">
										<SelectWithImage<Style>
											options={data.regularStyles}
											specialOptions={data.specialStyles}
											onSelect={handleStyleSelect}
										/>
									</div>
									{displayUserStyleNotes && (
										<div className="mt-2">
											<div className="flex flex-row justify-between">
												<label
													htmlFor="user-style-notes"
													className="block text-sm font-medium leading-6 text-gray-900"
												>
													Style Notes
												</label>
												<div className="hidden lg:inline-block">
													<TooltipStandard
														content={
															"Tell us which aspects of the style you like, or don't like." +
															" How is your pet's personality best reflected in the portrait?" +
															" This will help us match you with an artist and create a portrait you'll love."
														}
													/>
												</div>
												<div className="inline-block lg:hidden">
													<TooltipPopover
														title={'Style Notes'}
														content={
															"Tell us which aspects of the style you like, or don't like." +
															" How is your pet's personality best reflected in the portrait?" +
															" This will help us match you with an artist and create a portrait you'll love."
														}
													/>
												</div>
											</div>
											<input
												type="text"
												name="userStyleNotes"
												id="userStyleNotes"
												placeholder="Tell us what you like about this style. Lightbulb for more info!"
												className="mt-1.5 block w-full rounded-md border-0 py-1.5 pl-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
											/>
										</div>
									)}
								</div>

								{/* Example of style combinations */}
								<div className="mt-8">
									{selectedStyle ? (
										<h2 className="text-sm font-medium text-gray-900">
											Scenes popular with the {selectedStyle.label} style:
										</h2>
									) : null}
								</div>
								<div className="mt-2 grid grid-cols-3 gap-3">
									{exampleImagesStyle
										? exampleImagesStyle.map(image => (
												<div key={image.id}>
													<AdvancedImage
														cldImg={generateThumbnail(image.imgSrc)}
														alt={image.imgAlt}
														className="rounded-md object-cover object-center"
													/>
													<div className="py-1">
														<p className="text-xs font-semibold text-gray-600">
															{image.sceneName}
														</p>
													</div>
												</div>
										  ))
										: null}
								</div>

								{/* Scene Select */}
								<div className="mt-8">
									<div className="flex items-center justify-between">
										<h2 className="text-sm font-medium text-gray-900">Scene</h2>
										<div className="hidden lg:inline-block">
											<TooltipStandard
												content={
													'Scene is largely about the background, lighting, decoration and mood of the portrait.'
												}
											/>
										</div>
										<div className="inline-block lg:hidden">
											<TooltipPopover
												title={'Scene'}
												content={
													'Scene is largely about the background, lighting, decoration and mood of the portrait.'
												}
											/>
										</div>
									</div>
									<div className="hidden lg:inline-block">
										<SelectCommand<Scene>
											options={data.regularScenes}
											specialOptions={data.specialScenes}
											onSelect={handleSceneSelect}
											autoFocus={false}
										/>
									</div>
									<div className="lg:hidden">
										<SelectWithImage<Scene>
											options={data.regularScenes}
											specialOptions={data.specialScenes}
											onSelect={handleSceneSelect}
										/>
									</div>
									{displayUserSceneNotes && (
										<div className="mt-2">
											<div className="flex flex-row justify-between">
												<label
													htmlFor="user-scene-notes"
													className="block text-sm font-medium leading-6 text-gray-900"
												>
													Scene Notes
												</label>
												<div className="hidden lg:inline-block">
													<TooltipStandard
														content={
															"Tell us which aspects of the scene you like, or don't like." +
															' Which keywords for the scene spark your imagination?' +
															' This will help us create a portrait that will be a perfect fit in your space.'
														}
													/>
												</div>
												<div className="inline-block lg:hidden">
													<TooltipPopover
														title={'Scene Notes'}
														content={
															"Tell us which aspects of the scene you like, or don't like." +
															' Which keywords for the scene spark your imagination?' +
															' This will help us create a portrait that will be a perfect fit in your space.'
														}
													/>
												</div>
											</div>
											<input
												type="text"
												name="userSceneNotes"
												id="userSceneNotes"
												placeholder="Tell us what you like about this scene choice. Lightbulb for more info!"
												className="mt-1.5 block w-full rounded-md border-0 py-1.5 pl-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
											/>
										</div>
									)}
								</div>

								{/* Example of scene combinations */}
								<div className="mt-8">
									{selectedScene ? (
										<h2 className="text-sm font-medium text-gray-900">
											Styles popular with the {selectedScene.label} scene:
										</h2>
									) : null}
								</div>
								<div className="mt-2 grid grid-cols-3 gap-3">
									{exampleImagesScene
										? exampleImagesScene.map(image => (
												<div key={image.id}>
													<AdvancedImage
														cldImg={generateThumbnail(image.imgSrc)}
														alt={image.imgAlt}
														className="rounded-md object-cover object-center"
													/>
													<div className="py-1">
														<p className="text-xs font-semibold text-gray-600">
															{image.styleName}
														</p>
													</div>
												</div>
										  ))
										: null}
								</div>

								{/* User Notes */}
								<div className="mt-8">
									<div className="flex flex-row justify-between">
										<label
											htmlFor="user-notes"
											className="block align-text-bottom text-sm font-medium leading-6 text-gray-900"
										>
											Any other notes that you'd like your artist to consider?
										</label>
										<div className="hidden lg:inline-block">
											<TooltipStandard
												content={
													"This can be anything. You can describe your pet's personality" +
													' in more detail, or give some more description of the influences you want to see in the style.' +
													"We can't promise we'll get every detail exactly right, but we promise we'll try!"
												}
											/>
										</div>
										<div className="inline-block lg:hidden">
											<TooltipPopover
												title={'User Notes'}
												content={
													"This can be anything. You can describe your pet's personality" +
													' in more detail, or give some more description of the influences you want to see in the style.' +
													"We can't promise we'll get every detail exactly right, but we promise we'll try!"
												}
											/>
										</div>
									</div>
									<div className="mt-2">
										<textarea
											rows={4}
											name="userNotes"
											id="userNotes"
											placeholder="Any other notes for your artist about the style or your pet? We can't promise we'll get every detail exactly right, but we promise we'll try!"
											className="block w-full rounded-md border-0 py-1.5 pl-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
											defaultValue={''}
										/>
									</div>
								</div>

								<Button
									type="submit"
									color="deepGold"
									disabled={!formValid || submitting}
									className={clsx(
										'mt-8 flex w-full items-center justify-center rounded-md px-8 py-3 text-base font-medium',
										!formValid || submitting ? 'opacity-50' : 'text-white',
									)}
								>
									{submitting ? 'Loading...' : 'Save and Continue!'}
								</Button>
							</Form>

							{/* Product details */}
							<div className="mt-10">
								<h2 className="text-sm font-medium text-gray-900">
									Description
								</h2>

								<div
									className="prose prose-sm mt-4 text-gray-500"
									dangerouslySetInnerHTML={{ __html: data.product.description }}
								/>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	)
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function ErrorBoundary() {
	return (
		<GeneralErrorBoundary
			statusHandlers={{
				404: ({ params }) => (
					<p>No product with the productId "{params.productId}" exists</p>
				),
			}}
		/>
	)
}
